import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  aiApi,
  type ForecastListParams,
  type ChurnListParams,
  type AnomalyListParams,
  type ReplenishmentListParams,
  type SentimentPeriod,
} from "@/lib/admin-api/ai";

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

const KEYS = {
  dashboard: ["admin-ai-dashboard"] as const,
  models: ["admin-ai-models"] as const,
  forecast: (params: ForecastListParams) =>
    ["admin-ai-forecast", params] as const,
  pricing: ["admin-ai-pricing"] as const,
  churn: (params: ChurnListParams) => ["admin-ai-churn", params] as const,
  anomalies: (params: AnomalyListParams) =>
    ["admin-ai-anomalies", params] as const,
  replenishment: (params: ReplenishmentListParams) =>
    ["admin-ai-replenishment", params] as const,
  insights: ["admin-ai-insights"] as const,
  recommendations: ["admin-ai-recommendations"] as const,
  sentiment: (period: SentimentPeriod) =>
    ["admin-ai-sentiment", period] as const,
} as const;

// ---------------------------------------------------------------------------
// Dashboard
// ---------------------------------------------------------------------------

export function useAIDashboardKPIs() {
  return useQuery({
    queryKey: KEYS.dashboard,
    queryFn: () => aiApi.dashboard.kpis(),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Model Status (real registry)
// ---------------------------------------------------------------------------

export function useModelStatus() {
  return useQuery({
    queryKey: KEYS.models,
    queryFn: () => aiApi.models.status(),
    staleTime: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Demand Forecasting
// ---------------------------------------------------------------------------

export function useDemandForecast(params: ForecastListParams = {}) {
  return useQuery({
    queryKey: KEYS.forecast(params),
    queryFn: () => aiApi.forecast.list(params),
    staleTime: 120_000,
  });
}

export function useRefreshForecast() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => aiApi.forecast.refresh(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ai-forecast"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Smart Pricing
// ---------------------------------------------------------------------------

export function useSmartPricing() {
  return useQuery({
    queryKey: KEYS.pricing,
    queryFn: () => aiApi.pricing.suggestions(),
    staleTime: 60_000,
  });
}

export function useApplyPrice() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      productId,
      price,
    }: {
      readonly productId: string;
      readonly price: number;
    }) => aiApi.pricing.apply(productId, price),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.pricing });
    },
  });
}

export function useApplyAllPrices() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => aiApi.pricing.applyAll(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.pricing });
    },
  });
}

// ---------------------------------------------------------------------------
// Churn Prediction
// ---------------------------------------------------------------------------

export function useChurnPrediction(params: ChurnListParams = {}) {
  return useQuery({
    queryKey: KEYS.churn(params),
    queryFn: () => aiApi.churn.list(params),
    staleTime: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Anomaly Detection
// ---------------------------------------------------------------------------

export function useAnomalies(params: AnomalyListParams = {}) {
  return useQuery({
    queryKey: KEYS.anomalies(params),
    queryFn: () => aiApi.anomalies.list(params),
    staleTime: 30_000,
  });
}

export function useAcknowledgeAnomaly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiApi.anomalies.acknowledge(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ai-anomalies"] });
    },
  });
}

export function useResolveAnomaly() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiApi.anomalies.resolve(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-ai-anomalies"] });
    },
  });
}

// ---------------------------------------------------------------------------
// Replenishment Alerts
// ---------------------------------------------------------------------------

export function useReplenishmentAlerts(params: ReplenishmentListParams = {}) {
  return useQuery({
    queryKey: KEYS.replenishment(params),
    queryFn: () => aiApi.replenishment.list(params),
    staleTime: 60_000,
  });
}

// ---------------------------------------------------------------------------
// Sales Insights
// ---------------------------------------------------------------------------

export function useSalesInsights() {
  return useQuery({
    queryKey: KEYS.insights,
    queryFn: () => aiApi.insights.list(),
    staleTime: 120_000,
  });
}

// ---------------------------------------------------------------------------
// Recommendations
// ---------------------------------------------------------------------------

export function useRecommendations() {
  return useQuery({
    queryKey: KEYS.recommendations,
    queryFn: () => aiApi.recommendations.get(),
    staleTime: 60_000,
  });
}

export function useToggleRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiApi.recommendations.toggle(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.recommendations });
    },
  });
}

export function useRetrainRecommendation() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => aiApi.recommendations.retrain(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: KEYS.recommendations });
    },
  });
}

// ---------------------------------------------------------------------------
// Sentiment Analysis
// ---------------------------------------------------------------------------

export function useSentimentAnalysis(period: SentimentPeriod = "30d") {
  return useQuery({
    queryKey: KEYS.sentiment(period),
    queryFn: () => aiApi.sentiment.get(period),
    staleTime: 120_000,
  });
}
